import React, { useState, useEffect } from 'react';
import { Book, FileText, Video, Code, Download, ExternalLink, Search, Bookmark } from 'lucide-react';
import { supabase } from '../supabaseClient';

const Documentation = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    fetchDocumentation();
  }, []);

  const fetchDocumentation = async () => {
    try {
      const { data, error } = await supabase
        .from('documentation')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setDocs(data || []);
    } catch (error) {
      console.error('Error fetching documentation:', error);
    } finally {
      setLoading(false);
    }
  };

  const types = ['all', 'guide', 'tutorial', 'video', 'api', 'resource'];
  
  const filteredDocs = docs.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === 'all' || doc.type === selectedType;
    return matchesSearch && matchesType;
  });

  const getIcon = (type) => {
    switch(type) {
      case 'guide': return <Book size={28} className="text-gray-900" />;
      case 'tutorial': return <FileText size={28} className="text-gray-900" />;
      case 'video': return <Video size={28} className="text-gray-900" />;
      case 'api': return <Code size={28} className="text-gray-900" />;
      default: return <FileText size={28} className="text-gray-900" />;
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      guide: 'from-blue-400 to-blue-600',
      tutorial: 'from-green-400 to-green-600',
      video: 'from-purple-400 to-purple-600',
      api: 'from-orange-400 to-orange-600',
      resource: 'from-cyan-400 to-teal-500'
    };
    return colors[type] || 'from-cyan-400 to-teal-500';
  };

  return (
    <div className="min-h-screen py-16 relative">
      {/* Background decorative elements */}
      <div className="absolute top-40 right-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-40 left-10 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
      
      <div className="section-container relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-5 py-2.5 glass-card rounded-full text-sm font-semibold text-cyan-400 border border-cyan-500/20 mb-8">
            <Bookmark size={16} className="text-cyan-400" />
            <span>Learn & Master</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold font-poppins mb-6">
            Documentation & <span className="gradient-text">Resources</span>
          </h1>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto leading-relaxed">
            Everything you need to get started and master our products
          </p>
        </div>

        {/* Search and Filter */}
        <div className="glass-card p-6 md:p-8 mb-12 rounded-2xl border border-cyan-500/10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-cyan-400 transition" size={20} />
              <input
                type="text"
                placeholder="Search documentation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-gray-900/70 transition"
              />
            </div>

            {/* Type Filter */}
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-4 py-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-cyan-500 focus:bg-gray-900/70 transition appearance-none cursor-pointer"
            >
              <option value="all">All Types</option>
              {types.filter(t => t !== 'all').map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
          <div className="glass-card p-8 text-center card-hover cursor-pointer group relative overflow-hidden rounded-2xl border border-cyan-500/10">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-3xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-xl shadow-cyan-500/30">
                <Book size={32} className="text-gray-900" />
              </div>
              <h3 className="font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">Getting Started</h3>
              <p className="text-gray-400 text-sm">Quick start guides</p>
            </div>
          </div>
          <div className="glass-card p-8 text-center card-hover cursor-pointer group relative overflow-hidden rounded-2xl border border-cyan-500/10">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-green-400 to-green-600 rounded-3xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-xl shadow-green-500/30">
                <FileText size={32} className="text-gray-900" />
              </div>
              <h3 className="font-bold text-white mb-2 group-hover:text-green-400 transition-colors">Tutorials</h3>
              <p className="text-gray-400 text-sm">Step-by-step guides</p>
            </div>
          </div>
          <div className="glass-card p-8 text-center card-hover cursor-pointer group relative overflow-hidden rounded-2xl border border-cyan-500/10">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-purple-400 to-purple-600 rounded-3xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-xl shadow-purple-500/30">
                <Video size={32} className="text-gray-900" />
              </div>
              <h3 className="font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">Video Guides</h3>
              <p className="text-gray-400 text-sm">Visual tutorials</p>
            </div>
          </div>
          <div className="glass-card p-8 text-center card-hover cursor-pointer group relative overflow-hidden rounded-2xl border border-cyan-500/10">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative z-10">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-orange-400 to-orange-600 rounded-3xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-xl shadow-orange-500/30">
                <Code size={32} className="text-gray-900" />
              </div>
              <h3 className="font-bold text-white mb-2 group-hover:text-orange-400 transition-colors">API Docs</h3>
              <p className="text-gray-400 text-sm">API references</p>
            </div>
          </div>
        </div>

        {/* Documentation List */}
        {loading ? (
          <div className="text-center text-gray-400 py-32">
            <div className="relative w-20 h-20 mx-auto mb-8">
              <div className="absolute inset-0 border-4 border-cyan-500/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-xl font-semibold">Loading documentation...</p>
            <p className="text-sm text-gray-500 mt-2">Please wait while we prepare the resources</p>
          </div>
        ) : filteredDocs.length === 0 ? (
          <div className="text-center text-gray-400 py-32">
            <div className="glass-card max-w-md mx-auto p-12 rounded-3xl">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-cyan-400/20 to-teal-500/20 rounded-full flex items-center justify-center">
                <Book size={40} className="text-cyan-400" />
              </div>
              <p className="text-3xl font-bold mb-4">No documentation found</p>
              <p className="text-base text-gray-500">Check back soon for more resources</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredDocs.map((doc) => (
              <div 
                key={doc.id} 
                className="glass-card p-8 card-hover group cursor-pointer relative overflow-hidden rounded-2xl border border-cyan-500/10"
                data-testid={`doc-${doc.id}`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  <div className={`w-20 h-20 mb-6 bg-gradient-to-br ${getTypeColor(doc.type)} rounded-3xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-xl`}>
                    {getIcon(doc.type)}
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold rounded-full uppercase">
                      {doc.type}
                    </span>
                    {doc.is_new && (
                      <span className="px-4 py-1.5 bg-gradient-to-r from-green-500/20 to-teal-500/20 border border-green-500/30 text-green-400 text-xs font-bold rounded-full">
                        NEW
                      </span>
                    )}
                  </div>

                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                    {doc.title}
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed">
                    {doc.description}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-800/50">
                    <div className="text-gray-500 text-sm">
                      {doc.read_time || '5'} min read
                    </div>
                    <div className="flex items-center space-x-3">
                      {doc.download_url && (
                        <a 
                          href={doc.download_url}
                          className="p-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 rounded-lg transition"
                          aria-label="Download"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Download size={18} />
                        </a>
                      )}
                      {doc.url && (
                        <a 
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 rounded-lg transition"
                          aria-label="View"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink size={18} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Help CTA */}
        <div className="mt-20 glass-card p-16 text-center rounded-3xl border-2 border-cyan-500/20 relative overflow-hidden">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold font-poppins mb-4">
              Need More <span className="gradient-text">Help</span>?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Can't find what you're looking for? Our support team is here to help
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="gradient-button inline-flex items-center justify-center space-x-2 text-lg px-10 py-5 rounded-xl shadow-lg shadow-cyan-500/30">
                <span>Contact Support</span>
              </a>
              <a href="/faq" className="glass-card inline-flex items-center justify-center px-10 py-5 rounded-xl text-gray-300 hover:text-white hover:border-cyan-500/30 transition-all">
                <span>View FAQ</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;